import Moment from 'moment';
import CartUIModel from './cart';
import ClidUIModel from './clid';
import ElegibilityEMDUIModel from './elegibilityEMD';
import LegUIModel from './leg';
import LegCollectionUIModel from './leg-collection';
import LegOriginCollectionUIModel from './leg-origin-collection';
import PricePerPassengerTypeMapUIModel from './price-per-passenger-type-map';
import PI from '../../scripts/constants/personalized-insurance-types';
import { PREMIER } from '../../scripts/constants/premier-types';

class PNRUIModel {
  constructor({
    actionSubmitType = undefined,
    cart = undefined,
    clid = undefined,
    checkinStatus = undefined,
    creationDate = undefined,
    elegibilityEMD = undefined,
    flightItineraryType = undefined,
    globalMarketFlight = undefined,
    hoursToExpire = 0, // default hoursToExpire
    legs = undefined,
    legsOrigin = undefined,
    manageStatus = '', // default manageStatus
    pnr = undefined,
    pricePerPassengerTypeMap = undefined,
    reservationStatus = undefined,
    remarks = undefined,
    isCorporateFlow = undefined,
    corporateFlow = undefined,
  } = {}) {
    if (cart !== undefined) {
      cart['reservationStatus'] = reservationStatus;
      cart['pricePerPassengerTypeMap'] = pricePerPassengerTypeMap;
    }

    this.actionSubmitType = actionSubmitType;
    this.cart = new CartUIModel(cart);
    this.clid = clid ? new ClidUIModel(clid) : null;
    this.checkinStatus = checkinStatus;
    this.creationDate = null;
    if (creationDate) {
      Moment.locale('es-us');
      this.creationDate = Moment.utc(creationDate);
    }
    this.elegibilityEMD = elegibilityEMD ? new ElegibilityEMDUIModel(elegibilityEMD) : null;
    this.flightItineraryType = flightItineraryType;
    this.globalMarketFlight = globalMarketFlight;
    this.hoursToExpire = hoursToExpire;
    this.isCorporateFlow = isCorporateFlow;
    this.corporateFlow = corporateFlow;
    if (legs !== undefined) {
      this.legCollection = legs._collection.map((leg, index) => {
        const segmentLegsOrigin = legsOrigin && legsOrigin._collection && legsOrigin._collection[index] && legsOrigin._collection[index].segments && legsOrigin._collection[index].segments.legCode;
        leg.segmentLegsOrigin = segmentLegsOrigin;
        return new LegUIModel(leg);
      });
    }
    this.manageStatus = manageStatus;
    this.pnrCode = pnr;
    this.reservationStatus = reservationStatus;
    if (legs !== undefined) {
      this.legs = new LegCollectionUIModel(legs._collection);
    }
    if (legsOrigin !== undefined) {
      this.legsOrigin = new LegOriginCollectionUIModel(legsOrigin._collection);
    }

    this.pricePerPassengerTypeMap = pricePerPassengerTypeMap ? new PricePerPassengerTypeMapUIModel(pricePerPassengerTypeMap) : null;
    this.remarks = remarks;
    this.isFareLock = this._getIsFareLock();
  }

  // GETTERS

  /**
  * @Return {String} Destination first leg
  */
  getDestinationCode() {
    let code = this.legCollection[0].getDestinationCode();
    if (this.flightItineraryType === 'MULTICITY') {
      code = this._getNextLeg().getDestinationCode();
    }
    return code;
  }

  /**
  * @Return {String} Destination first leg
  */
  getAllDestinationsCode() {
    const allDestination = this.legCollection.map(legModel => {
      return legModel.getDestinationCode();
    });
    const destinationArray = [];
    return allDestination.filter(des => {
      if (!destinationArray?.includes(des)) {
        destinationArray.push(des);
        return des;
      }
    });
  }

  getOriginCode() {
    let code = this.legCollection[0].getOriginCode();
    if (this.flightItineraryType === 'MULTICITY') {
      code = this._getNextLeg().getOriginCode();
    }
    return code;
  }

  getAllItinerary() {
    let query = '';

    this.legCollection.forEach(legModel => {
      const date = legModel?.legCode?.slice(-10);
      const destinationCode = legModel?.getDestinationCode();
      const originCode = legModel?.getOriginCode();
      query += `${originCode}_${destinationCode}_${date}.`;
    });

    query = query.slice(0, -1);

    if (this.flightItineraryType === 'MULTICITY') {
      query = 'multiCity=true&itinerary=' + query;
    } else {
      query = 'itinerary=' + query;
    }

    return query;
  }


  /**
  * @Return {Object} month and day of arrivalDate
  */
  getArrivalDate(format) {
    let date = this.legCollection[0].getArrivalDate(format);
    if (this.flightItineraryType === 'ROUNDTRIP' || this.flightItineraryType === 'MULTICITY') {
      date = this.legCollection[1].getArrivalDate(format);
      if (this.legCollection[0].itineraryPartType === 'MULTICITY') {
        const legProx = this._getNextLeg();
        date = legProx.getArrivalDate(format);
      }
    }
    return date;
  }

  getDepartureDateLastSegment(format, language = '') {
    const indexArrival = this.legCollection.length - 1;
    return this.legCollection[indexArrival].getDepartureDate(format, language);
  }

  /**
  * @Return {String} Arrival first segment
  */
  getDepartureDate(format, language = '') {
    let date = this.legCollection[0].getDepartureDate(format, language);
    if (this.flightItineraryType === 'MULTICITY') {
      const legProx = this._getNextLeg();
      date = legProx.getDepartureDate(format);
    }
    return date;
  }

  getListFlightInfo() {
    const arrListFlightInfo = [];
    for (const leg of this.legs._legCollection) {
      for (const segment of leg.segmentsCollection) {
        if ((segment.isChangeOag || segment.isChangeOds) && segment.segmentStatus === 'SC') {
          arrListFlightInfo.push({
            'airlineCode': segment.marketingCarrier,
            'flightNumber': segment.marketingFlightCode,
            'departureDateTime': Moment(segment.departureDateTime).format('YYYY-MM-DDTHH:mm:ss'),
            'arrivalDateTime': Moment(segment.arrivalDateTime).format('YYYY-MM-DDTHH:mm:ss'),
            'isFake': true,
          });
        }
      }
    }

    return arrListFlightInfo;
  }

  isSameMonthDepartureArrivalDate() {
    const legs = this._getLegs();
    const indexArrival = legs.arrival.segmentsCollection.length - 1;
    return legs.departure.segmentsCollection[0].departureDateTime.month() === legs.arrival.segmentsCollection[indexArrival].departureDateTime.month();
  }

  isSameDayDepartureArrivalDate() {
    const legs = this._getLegs();
    const indexArrival = legs.arrival.segmentsCollection.length - 1;
    return legs.departure.segmentsCollection[0].departureDateTime.date() === legs.arrival.segmentsCollection[indexArrival].departureDateTime.date();
  }

  isOneWayWithConnection() {
    return this.flightItineraryType === 'ONEWAY' && this.legCollection[0].legType === 'CONNECTING';
  }

  isMulticity() {
    return this.legCollection[0].itineraryPartType === 'MULTICITY' || this.flightItineraryType === 'MULTICITY';
  }

  allLegsAreCodeShare() {
    let isCodeShare = true;
    this.legCollection.forEach(leg => {
      if (!leg.isCodeShare()) {
        isCodeShare = false;
      }
    });
    return isCodeShare;
  }

  isPnrCodeShare() {
    return this.legCollection.some(leg => leg.isCodeShare());
  }

  allLegsAreAM() {
    let isLegAM = true;
    this.legCollection.forEach(leg => {
      if (leg.isCodeShare()) {
        isLegAM = false;
      }
    });
    return isLegAM;
  }

  getPaidCBX() {
    return this.cart.getPaidCBX(this.getLegCodesForAllLegsAvailable('MYB'));
  }

  getQRCodeCBX() {
    return this.cart.getQRCodeCBX(this.getLegCodesForAllLegsAvailable('MYB'));
  }

  hasUnpaidAncillary() {
    if (!this.cart || typeof this.cart.hasUnpaidAncillary !== 'function') {
      return false;
    }
    return this.cart.hasUnpaidAncillary();
  }

  hasUnpaidAncillaryPP() {
    if (!this.cart || typeof this.cart.hasUnpaidAncillaryPP !== 'function') {
      return false;
    }
    return this.cart.hasUnpaidAncillaryPP();
  }

  hasUnpaidBaggagePP() {
    return this.cart.hasUnpaidBaggagePP();
  }

  hasUnpaidCarryOnPP() {
    return this.cart.hasUnpaidCarryOnPP();
  }

  hasUnpaidFlyGreen() {
    return this.cart.hasUnpaidFlyGreen();
  }

  hasUnpaidPriority() {
    return this.cart.hasUnpaidPriority();
  }

  hasUnpaidUpgrade() {
    return this.cart.hasUnpaidUpgrade();
  }

  hasUnpaidUpgradePP() {
    return this.cart.hasUnpaidUpgradePP();
  }

  hasUnpaidSeat() {
    return this.cart.hasUnpaidSeat();
  }

  hasUnpaidSeatPP() {
    return this.cart.hasUnpaidSeatPP();
  }

  getAllUnPaidAncillaries() {
    return this.cart.getAllUnPaidAncillaries();
  }

  getUnPaidAncillaries() {
    return this.cart.getUnPaidAncillaries();
  }

  getUnPaiFlyGreenAncillaries() {
    return this.cart.getUnPaiFlyGreenAncillaries();
  }

  getUnPaiPRIdAncillaries() {
    return this.cart.getUnPaiPRIdAncillaries();
  }

  getBaggageUnPaidAncillaries() {
    return this.cart.getBaggageUnPaidAncillaries();
  }

  getCarryOnPaidAncillaries() {
    return this.cart.getCarryOnPaidAncillaries();
  }

  getBaggagePaidAncillaries() {
    return this.cart.getBaggagePaidAncillaries();
  }

  getExtraBaggagePaidAncillaries() {
    return this.cart.getExtraBaggagePaidAncillaries();
  }

  getBaggageUnPaidAncillariesPP() {
    return this.cart.getBaggageUnPaidAncillariesPP();
  }

  getCarryOnUnPaidAncillaries() {
    return this.cart.getCarryOnUnPaidAncillaries();
  }

  getCarryOnUnPaidAncillariesPP() {
    return this.cart.getCarryOnUnPaidAncillariesPP();
  }

  getSpecialBaggageUnPaidAncillaries() {
    return this.cart.getSpecialBaggageUnPaidAncillaries();
  }

  getWeightBagByTravel(type, travelerIndex, legCode) {
    return this.cart.getWeightBagByTravel(type, travelerIndex, legCode);
  }

  getWeightCarryOnByTravel(type, travelerIndex, legCode) {
    return this.cart.getWeightCarryOnByTravel(type, travelerIndex, legCode);
  }

  getAllRoutes() {
    let routes = [];
    this.legCollection.forEach(legModel => {
      if (legModel.manageStatus === 'OPEN') {
        routes = routes.concat(legModel.getRoutes());
      }
    });
    return routes;
  }

  getAllRoutesAvailablesForSeatMap() {
    let routes = [];
    this.legCollection.forEach(legModel => {
      if (legModel.manageStatus === 'OPEN') {
        routes = routes.concat(legModel.getRoutesAvailablesForSeatMap());
      }
    });
    return routes;
  }

  getAllPassengers() {
    return this.cart.getAllPassengers();
  }

  isSomePassengerAvailableForInsurance() {
    const allPassengers = this.getAllPassengers();
    if (allPassengers) {
      return allPassengers.some(passenger => passenger.isInsuranceAvailableForTraveler(this.getDepartureDateLastSegment()));
    } else {
      return false;
    }
  }

  getTravelerById(travelerId) {
    return this.getAllPassengers().find(traveler => {
      return traveler.id === travelerId;
    });
  }

  getUnPaidSegmentChoice(segments) {
    return this.cart.getUnPaidSegmentChoice(segments);
  }

  getUnPaidSegmentChoiceLength() {
    return this.cart.getUnPaidSegmentChoiceLength();
  }

  getCurrencyCode() {
    return this.cart.getCurrencyCode();
  }

  /*
   * @param type MYB, CHEKIN
   * @return {int} total segments available by type
   * @return by default total all segments (OPEN, CLOSED)
  */
  getNumberSegments(type) {
    let numberSegments = 0;
    this.legCollection.forEach(legModel => {
      switch (type) {
        case 'MYB':
          numberSegments += legModel.manageStatus === 'OPEN' ? legModel.segmentsCollection.length : 0;
          break;
        case 'CHEKIN':
          numberSegments += legModel.checkinStatus === 'OPEN' ? legModel.segmentsCollection.length : 0;
          break;
        default:
          numberSegments += legModel.segmentsCollection.length;
          break;
      }
    });
    return numberSegments;
  }

  /*
   * @param type MYB, CHEKIN
   * @return array segments OPEN for type
   * @return by default all segments (OPEN, CLOSED)
  */
  getSegmentsAvailable(type, hasTJX) {
    let segmentsCollection = new Array();
    this.legCollection.forEach(legModel => {
      switch (type) {
        case 'CHEKIN':
          segmentsCollection = legModel.checkinStatus === 'OPEN' ? segmentsCollection.concat(legModel.segmentsCollection) : segmentsCollection;
          break;
        case 'MYB':
        default:
          segmentsCollection = legModel.manageStatus === 'OPEN' ? segmentsCollection.concat(legModel.segmentsCollection) : segmentsCollection;
      }
    });

    if (hasTJX) {
      const segmentsNoTJXCollection = segmentsCollection.filter(segment =>
        !(segment.arrivalAirport === 'TJX' || segment.departureAirport === 'TJX')
      );
      segmentsCollection = segmentsNoTJXCollection;
    }

    return segmentsCollection;
  }

  getSegmentsAvailablePriority() {
    let segmentsCollection = new Array();

    this.legCollection.forEach(legModel => {
      if (legModel.manageStatus === 'OPEN') {
        segmentsCollection = segmentsCollection.concat(legModel.segmentsCollection);
      }
    });

    segmentsCollection = segmentsCollection.filter(segItem => {
      if (segItem.fareFamily === 'BASIC' || 
          segItem.fareFamily === 'BASICLIGHT' || 
          segItem.fareFamily === 'CLASSIC') {
        return segItem;
      }
    });

    return segmentsCollection;
  }

  getStringSegment(segmentCode) {
    const arraySegmentCode = segmentCode.split('_');
    arraySegmentCode.pop();

    return arraySegmentCode.join('_');
  }

  getExtrasUpgradeBySegment(segmentCode) {
    let premierType = PREMIER.NO_OFFER;
    let currentSegment = null;

    this.legCollection.forEach((legModel) => {
      legModel.segmentsCollection.forEach((segment) => {
        const stringSegmentCode = this.getStringSegment(segmentCode);
        const currentStringSegmentCode = this.getStringSegment(segment.segmentCode);
        if (stringSegmentCode === currentStringSegmentCode) {
          currentSegment = segment;
        }
      });
    });


    if (currentSegment) {
      const {
        isPremierLight,
        isPremierOne,
      } = currentSegment;

      premierType = isPremierOne ? PREMIER.ONE : isPremierLight ? PREMIER.LIGHT : PREMIER.PREMIER;
    }

    return premierType;
  }

  getSegmentByCode(segmentCode) {
    let segment = null;
    this.legCollection.forEach(legModel => {
      if (legModel.getSegmentByCode(segmentCode) !== undefined) {
        segment = legModel.getSegmentByCode(segmentCode);
      }
    });
    return segment;
  }

  getCurrentInsurance(dataBaggage) {
    const {
      travelerCollection,
    } = this.cart;

    let currentProtection = PI.UNPROTECTED;
    const isFlexible = this.getIsFlexibleFare();
    const showBaggageInsuranceArray = [];
    let isOnlyBaggage = false;
    let isOnlyCancel = false;
    let isUnprotected = false;

    let numberOfPassengers = 0;
    let countTravelersAMS = 0;
    let countTravelersSEP = 0;
    let countTravelersSXP = 0;
    let countTravelersSDE = 0;
    let countTravelersSXC = 0;


    travelerCollection.filter(collection => collection.isInsuranceAvailableForTraveler()).forEach((collection, index) => {
      numberOfPassengers += 1;
      const { ancillaries: { ancillaries } } = collection;
      const newAncillaries = [...ancillaries];

      const travelerHasAMSProt = newAncillaries.some((ac) => ac?.ancillary?.type === PI.AMS || (ac?.type === PI.AMS && ac?.isPaid));
      if (travelerHasAMSProt) {
        countTravelersAMS += 1;
      }
      const travelerHasPartialPlusProtSEP = newAncillaries.some((ac) => ac?.ancillary?.type === PI.SEP || (ac?.type === PI.SEP && ac?.isPaid));
      if (travelerHasPartialPlusProtSEP) {
        countTravelersSEP += 1;
      }
      const travelerHasPartialPlusProtSXP = newAncillaries.some((ac) => ac?.ancillary?.type === PI.SXP || (ac?.type === PI.SXP && ac?.isPaid));
      if (travelerHasPartialPlusProtSXP) {
        countTravelersSXP += 1;
      }

      const travelerHasPartialPlusProtSDE = newAncillaries.some((ac) => ac?.ancillary?.type === PI.SDE || (ac?.type === PI.SDE && ac?.isPaid));
      if (travelerHasPartialPlusProtSDE) {
        countTravelersSDE += 1;
      }

      const travelerHasPartialPlusProtSXC = newAncillaries.some((ac) => ac?.ancillary?.type === PI.SXC || (ac?.type === PI.SXC && ac?.isPaid));
      if (travelerHasPartialPlusProtSXC) {
        countTravelersSXC += 1;
      }

      let passengerFreeBaggage = 0;
      // eslint-disable-next-line no-unused-expressions
      Object.keys(dataBaggage)?.forEach(leg => {
        Object.keys(dataBaggage?.[leg]).forEach(traveler => {
          if (Math.floor(traveler) === (index + 1)) {
            passengerFreeBaggage += dataBaggage?.[leg]?.[traveler]?.freeBaggage;
          }
        });
      });

      const hasAncillaryBaggage = collection?.getBaggageAncillary()?.length > 0;
      const hasTicketedBaggage = collection?.getBaggageTicketedAncillaries()?.length > 0;
      const hasSpecialBaggage = collection?.getSpecialBaggageAncillary()?.length > 0;
      const hasFreeBaggage = passengerFreeBaggage > 0;
      const showBaggageInsurance = hasAncillaryBaggage || hasTicketedBaggage || hasSpecialBaggage || hasFreeBaggage;

      showBaggageInsuranceArray.push(showBaggageInsurance);
    });

    if (countTravelersAMS === numberOfPassengers) {
      currentProtection = PI.BASIC;
      const numberOfPassengersWithBaggage = showBaggageInsuranceArray.filter((value) => value).length;

      if (countTravelersSXP === numberOfPassengers && !countTravelersSXC && !countTravelersSEP && !countTravelersSDE) {
        currentProtection = PI.PARTIAL_PLUS_SXP;
      } else if (countTravelersSEP === numberOfPassengersWithBaggage && numberOfPassengersWithBaggage > 0 && !countTravelersSXC && !countTravelersSXP && !countTravelersSDE) {
        currentProtection = PI.PARTIAL_PLUS_SEP;
      } else if (countTravelersSXP >= 1 && countTravelersSEP >= 1 && ((countTravelersSXP + countTravelersSEP) >= numberOfPassengers)) {
        currentProtection = PI.MIX_PLUS;
      }

      if (((countTravelersSEP === numberOfPassengersWithBaggage && countTravelersSXP === numberOfPassengers)
        || (countTravelersSDE === numberOfPassengers && countTravelersSXC === numberOfPassengers))
      ) {
        currentProtection = PI.PLUS;
        isOnlyCancel = true;
        if (numberOfPassengersWithBaggage > 0) {
          isOnlyBaggage = true;
        }
      } else if (isFlexible && !showBaggageInsuranceArray.some((element) => !element)
        && countTravelersSEP === numberOfPassengersWithBaggage) {
        currentProtection = PI.PLUS;
        isOnlyBaggage = true;
      } else if (!isFlexible && showBaggageInsuranceArray.some((element) => !element)
        && countTravelersSXP === numberOfPassengers) {
        currentProtection = PI.PLUS;
        isOnlyCancel = true;
      }
    } else if ((countTravelersSXC || countTravelersSEP || countTravelersSXP || countTravelersSDE)
      && (countTravelersAMS > 0 && countTravelersAMS < numberOfPassengers)) {
      currentProtection = PI.MIX_INSURANCE;
    } else if ((countTravelersSXC || countTravelersAMS || countTravelersSEP || countTravelersSXP || countTravelersSDE)
      && (currentProtection === PI.UNPROTECTED)) {
      isUnprotected = true;
    }

    return { currentProtection, isOnlyBaggage, isOnlyCancel, isUnprotected };
  }

  isOfferedPersonalizedInsurance() {
    // Identifica si es Flexible
    const isFlexible = this.getIsFlexibleFare();
    let hasBaggage = true;
    const {
      travelerCollection,
    } = this.cart;

    // Revisar si es necesario revisar si el usuario tiene maletas
    travelerCollection.forEach(({ ancillaries }) => {
      const {
        ancillaries: travelerAncillaries,
      } = ancillaries;
      hasBaggage = travelerAncillaries.some((ac) => ac.type === '0CD' && ac.isPaid);
    });

    return !(isFlexible && !hasBaggage);
  }

  getIsFlexibleFare() {
    let isFlexible = null;
    this.legCollection.forEach(leg => {
      isFlexible = leg?.segmentsCollection?.some(({ fareAndCabinName }) =>
        fareAndCabinName?.includes('FLEX') || fareAndCabinName?.includes('FLEXIBLE')
      );
    });
    return isFlexible;
  }

  getPassengerAge(date) {
    const today = new Date();
    const birthDate = new Date(date);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  getUpgradeAvailableSegments() {
    const {
      legCollection,
    } = this;
    const {
      upsellOffers,
    } = this.cart;

    const segments = [];

    if (legCollection && legCollection.length > 0) {
      legCollection.forEach(leg => {
        const {
          segmentsCollection,
        } = leg;

        segmentsCollection.forEach(segment => {
          const {
            bookingClass,
            fareFamily,
            segmentCode,
          } = segment;

          const isUpgradeAvailableBySegmentCode = upsellOffers && upsellOffers.length > 0 && upsellOffers.some(upg => upg.segmentCode === segmentCode && upg.upgradeAvailable);
          const isUpgradeOfferAvailable = isUpgradeAvailableBySegmentCode && fareFamily && !fareFamily?.includes('PREMIER') && bookingClass !== 'F';
          if (isUpgradeOfferAvailable) {
            segments.push(segment);
          }
        });
      });
    }

    return segments;
  }

  getUpgradeAvailableSegmentsCodesArray() {
    const upgAvailableSegmentCodesArray = [];
    const upgradeAvailableSegments = this.getUpgradeAvailableSegments();

    for (const element of upgradeAvailableSegments) {
      upgAvailableSegmentCodesArray.push(element.segmentCode);
    }

    return upgAvailableSegmentCodesArray;
  }

  getUpgradeOfferType() {
    const cabinTypeArray = [];
    const upgradeAvailableSegments = this.getUpgradeAvailableSegments();
    const upgradeOfferArray = [];
    let upgradeType = PREMIER.NO_OFFER;
    let cabinAircrafts = [];
    if (upgradeAvailableSegments && upgradeAvailableSegments.length > 0) {
      upgradeAvailableSegments.forEach(segment => {
        const {
          aircraftType,
          isPremierLight,
          isPremierOne,
        } = segment;
        const premierType = isPremierOne ? PREMIER.ONE : isPremierLight ? PREMIER.LIGHT : PREMIER.PREMIER;
        upgradeOfferArray.push(premierType);
        cabinTypeArray.push(aircraftType);
      });

      const upgradeOfferNotDuplicate = new Set(upgradeOfferArray);
      const upgradeOffer = [...upgradeOfferNotDuplicate];

      const cabinTypeNotDuplicate = new Set(cabinTypeArray);
      cabinAircrafts = [...cabinTypeNotDuplicate];

      if (upgradeOffer.length > 0) {
        upgradeType = this.getUpgradeTypeOffer(upgradeOffer);
      }
    }

    return {
      cabinAircrafts,
      upgradeType,
    };
  }

  getUpgradeTypeOffer(upgradeOffer) {
    let upgradeType = '';
    if (upgradeOffer.length === 1 && upgradeOffer?.includes(PREMIER.ONE)) {
      upgradeType = PREMIER.ONE;
    } else if (upgradeOffer.length > 1 && upgradeOffer.some(upg => upg === PREMIER.ONE)) {
      upgradeType = PREMIER.ONE_MIXED;
    } else if (upgradeOffer?.includes(PREMIER.PREMIER) && upgradeOffer.some(upg => upg === PREMIER.LIGHT)) {
      upgradeType = PREMIER.PREMIER_MIXED;
    } else if (upgradeOffer.some(upg => upg === PREMIER.PREMIER)) {
      upgradeType = PREMIER.PREMIER;
    } else {
      upgradeType = PREMIER.LIGHT;
    }

    return upgradeType;
  }

  getUpgradeTypeBySegments(segmentCodes) {
    if (segmentCodes && segmentCodes.length > 0) {
      const upgradeTypeArray = [];

      segmentCodes.forEach(segmentCode => {
        const segment = this.getSegmentByCode(segmentCode);

        if (segment) {
          const {
            isPremierLight,
            isPremierOne,
          } = segment;

          const premierType = isPremierOne ? PREMIER.ONE : isPremierLight ? PREMIER.LIGHT : PREMIER.PREMIER;
          upgradeTypeArray.push(premierType);
        }
      });

      const upgradeTypeNotDuplicate = new Set(upgradeTypeArray);
      const upgradeType = [...upgradeTypeNotDuplicate];

      return this.getUpgradeTypeOfferBySegment(upgradeType);
    } else {
      return PREMIER.PREMIER;
    }
  }

  getUpgradeTypeOfferBySegment(upgradeType) {
    if (upgradeType.length > 0) {
      if (upgradeType.length === 1 && upgradeType?.includes(PREMIER.ONE)) {
        return PREMIER.ONE;
      } else if (upgradeType.length > 1 && upgradeType.some(upg => upg === PREMIER.ONE)) {
        return PREMIER.ONE_MIXED;
      } else if (upgradeType.some(upg => upg === PREMIER.PREMIER)) {
        return PREMIER.PREMIER;
      } else {
        return PREMIER.LIGHT;
      }
    } else {
      return PREMIER.PREMIER;
    }
  }

  getPremiereFareBySegmentCode(segmentCode, legCode) {
    const legModel = this.legCollection.find(leg => leg.getSegmentByCode(legCode));
    return legModel && legModel.getPremierTypeBySegmentCode(segmentCode);
  }

  isPremierLightBySegmentAirports(segmentCode, legCode) {
    let leg = null;
    let segment = null;
    this.legCollection.forEach(legModel => {
      const segmentCodeString = legModel.getSegmentByAirport(segmentCode);

      if (legModel.legCode === legCode && segmentCodeString) {
        leg = legModel;
        segment = segmentCodeString;
      }
    });

    return leg && leg.isPremierLightBySegmentCode(segment);
  }

  getPremierFareBySegmentAirports(segmentCode, legCode) {
    let leg = null;
    let segment = null;

    this.legCollection.forEach(legModel => {
      const segmentCodeString = legModel.getSegmentByAirport(segmentCode);
      if (legModel.legCode === legCode && segmentCodeString) {
        legModel.segmentsCollection.forEach((element) => {
          if (element.segmentCode.indexOf(segmentCode)) {
            leg = legModel;
            segment = element.segmentCode;
          }
        });
      }
    });

    return leg && leg.getPremierTypeBySegmentCode(segment);
  }

  getSegmentCodesForAllSegments() {
    const segmentCodes = new Array();
    this.legCollection.forEach(legModel => {
      legModel.segmentsCollection.forEach(segmentModel => {
        segmentCodes.push(segmentModel.segmentCode);
      });
    });
    return segmentCodes;
  }

  getLegByCode(legCode) {
    return this.legCollection.find(legModel => {
      return legModel.legCode === legCode;
    });
  }

  getLegBySegmentCode(segmentCode) {
    return this.legCollection.find(legModel => {
      return legModel.getSegmentByCode(segmentCode) !== undefined;
    });
  }

  getLegCodesForAllLegs() {
    const legCodes = new Array();
    this.legCollection.forEach(legModel => {
      legCodes.push(legModel.legCode);
    });
    return legCodes;
  }

  /*
   * @param type MYB, CHEKIN
   * @return array legCodes OPEN for type
   * @return by default all legCodes (OPEN, CLOSED)
  */
  getLegCodesForAllLegsAvailable(type) {
    const legCodes = new Array();
    this.legCollection.forEach(legModel => {
      switch (type) {
        case 'MYB':
          if (legModel.manageStatus === 'OPEN') {
            legCodes.push(legModel.legCode);
          }
          break;
        case 'CHEKIN':
          if (legModel.checkinStatus === 'OPEN') {
            legCodes.push(legModel.legCode);
          }
          break;
        default:
          legCodes.push(legModel.legCode);
          break;
      }
    });
    return legCodes;
  }

  /*
   * @return array legCodes OPEN for MYB and can offer Ancillary 029 *CBX*
  */
  getLegCodesAvailablesCBX() {
    const legCodes = new Array();
    this.legCollection.forEach(legModel => {
      if (legModel.manageStatus === 'OPEN' && legModel.canOfferCBX()) {
        legCodes.push(legModel.legCode);
      }
    });
    return legCodes;
  }

  getPremierTypeBySegmentCode(segmentCode) {
    const legModel = this.legCollection.find(legModel => legModel.getSegmentByCode(segmentCode));
    return legModel ? legModel.getPremierTypeBySegmentCode(segmentCode) : PREMIER.NO_OFFER;
  }

  getUpgradeCopyID(segmentCode, flow, ancillaryDescription) {
    const premierType = this.getPremierTypeBySegmentCode(segmentCode);

    switch (premierType) {
      case PREMIER.ONE:
        if (flow === 'ShoppingCart' || flow === 'Confirmation' || flow === 'ItineraryTickets') {
          return 'MYB_CART_PREMONE_ASCENT';
        }
        break;
      case PREMIER.LIGHT:
        if (flow === 'ShoppingCart' || flow === 'Confirmation') {
          return 'MYB_UPGRADE_ELEMENT_PREMIER_LIGHT';
        } else if (flow === 'ItineraryTickets') {
          return 'MYB_UPGRADE_PREMIER_LIGHT';
        }
        break;
      case PREMIER.PREMIER:
        if (flow === 'ShoppingCart' || flow === 'Confirmation') {
          return 'MYB_UPGRADE_PREMIER_CLASS';
        } else if (flow === 'ItineraryTickets') {
          return ancillaryDescription.headline;
        }
        break;
      default:
        return ancillaryDescription.headline;
    }
  }

  setTravelerCollection(travelerCollection) {
    this.cart.setTravelerCollection(travelerCollection);
  }

  setReservationStatus(reservationStatus) {
    this.reservationStatus = reservationStatus;
  }

  setCartId(cartId) {
    this.cart.cartId = cartId;
  }

  setCart(newCart) {
    this.cart.setCart(newCart);
  }

  clearSessionCart() {
    this.cart.clearSessionCart();
    return this;
  }

  travelerInfoToApi() {
    return {
      '_meta': { 'class': 'CartPNRUpdate' },
      'travelerInfo': {
        '_meta': { 'class': 'BookedTravelerCollection' },
        '_collection': this.cart.travelerInfoToApi(),
      },
    };
  }

  _getLegs() {
    let legDeparture = this.legCollection[0];
    let legArrival = this.legCollection[0];
    if (this.flightItineraryType === 'ROUNDTRIP' || this.flightItineraryType === 'MULTICITY') {
      legArrival = this.legCollection[1];
      if (this.flightItineraryType === 'MULTICITY') {
        legArrival = this._getNextLeg();
        legDeparture = this._getNextLeg();
      }
    }
    return {
      'departure': legDeparture,
      'arrival': legArrival,
    };
  }

  _getNextLeg() {
    const now = Moment();
    const newLegCollection = this.legCollection?.find(leg => leg?.segmentsCollection?.[0]?.departureDateTime > now);

    return newLegCollection || this.legCollection[this.legCollection.length - 1];
  }

  getMealsSelectionRoutes() {
    const allRoutes = this.getAllRoutes();

    const mealsAllowedSegments = this.cart.getMealsAllowedSegments();
    let routes = [];
    mealsAllowedSegments.forEach(mealSegment => {
      const mealRoute = allRoutes.find(route => route.segmentCode === mealSegment.segmentCodeAllowed);
      if (mealRoute !== undefined) {
        routes = routes.concat(mealRoute);
      }
    });
    routes = routes.filter((item, index) => routes.indexOf(item) === index);
    return routes;
  }

  /**
   * @param String segmentCode
   * @return String region of leg
   */
  getRegionBySegmentCode(segmentCode) {
    let region = undefined;
    this.legCollection.forEach(leg => {
      leg.segmentsCollection.forEach(segment => {
        if (segment.segmentCode === segmentCode) {
          region = segment.regionKeyFromLeg;
        }
      });
    });

    return region;
  }

  getAllDepartureAirports() {
    const allDepartureAirports = [];
    this.legCollection.map(legModel => {
      legModel.segmentsCollection.map(segmentModel => {
        allDepartureAirports.push(segmentModel.departureAirport);
      });
    });
    return allDepartureAirports;
  }

  getAllArrivalAirports() {
    const allArrivalAirports = [];
    this.legCollection.map(legModel => {
      legModel.segmentsCollection.map(segmentModel => {
        allArrivalAirports.push(segmentModel.arrivalAirport);
      });
    });
    return allArrivalAirports;
  }

  getAllFareFamilies() {
    const allFareFamilies = [];
    this.legCollection.map(legModel => {
      allFareFamilies.push(legModel.segmentsCollection[0].fareFamily);
    });
    return allFareFamilies;
  }

  // Check for Basic Light fare family from API
  isBasicLightFare() {
    const isDomestic = this.globalMarketFlight === 'DOMESTIC';
    const firstSegment = this.legCollection?.[0]?.segmentsCollection?.[0];

    if (!isDomestic || !firstSegment) {
      return false;
    }
    return firstSegment?.fareFamily === 'BASICLIGHT' || firstSegment?.fareFamily === 'MAIN_LIGHT';
  }

  getAllOutboundAndInboundDates() {
    const legCollection = this.legCollection;
    const outboundSegments = legCollection?.[0]?.segmentsCollection;
    const inboundSegments = legCollection?.[legCollection?.length - 1]?.segmentsCollection;

    const outboundDepartureDate = this.flightItineraryType === 'MULTICITY' ?
      (legCollection || []).map(leg => leg?.segmentsCollection?.[0]?.departureDateTime?.format('YYYYMMDD') || 'NA') :
      outboundSegments?.[0]?.departureDateTime?.format('YYYYMMDD') || 'NA';

    const outboundArrivalDate = this.flightItineraryType === 'MULTICITY' ?
      (legCollection || []).map(leg => leg?.segmentsCollection?.[leg?.segmentsCollection?.length - 1]?.arrivalDateTime?.format('YYYYMMDD') || 'NA') :
      outboundSegments?.[outboundSegments?.length - 1]?.arrivalDateTime?.format('YYYYMMDD') || 'NA';

    const inboundArrivalDate = this.flightItineraryType === 'ROUNDTRIP' ?
      inboundSegments?.[inboundSegments?.length - 1].arrivalDateTime?.format('YYYYMMDD') || 'NA' :
      'NA';

    const inboundDepartureDate = this.flightItineraryType === 'ROUNDTRIP' ?
      inboundSegments?.[inboundSegments?.length - 1].departureDateTime?.format('YYYYMMDD') || 'NA' :
      'NA';

    return {
      inboundArrivalDate,
      inboundDepartureDate,
      outboundDepartureDate,
      outboundArrivalDate,
    };
  }

  getAllMarketSegment() {
    return (this.legCollection || []).map((element) => {
      const segmentsInfo = element?.segmentsCollection;
      return (segmentsInfo || []).map((segmentsData) => {
        return `${segmentsData?.departureAirport}${segmentsData?.arrivalAirport}`;
      }).join('/');
    }).join('-');
  }

  travelersWithSegmentChoices() {
    return this.cart.travelerCollection.find(traveler => traveler.segmentChoices !== null && traveler.segmentChoices.items.length);
  }

  hasEMDVoucher() {
    return this.cart.hasEMDVoucher();
  }

  isValidEmdVoucher() {
    return this.cart.isValidEmdVoucher();
  }

  getNumberVoucher() {
    return this.cart.getNumberVoucher();
  }

  getErrorVoucher() {
    return this.cart.getErrorVoucher();
  }

  getVoucher() {
    return this.cart.emdVoucherRs;
  }

  getTotalEMDVoucher() {
    return this.cart.getTotalEMDVoucher();
  }

  getTotalForPaymentEMDVoucher() {
    return this.cart.getTotalForPaymentEMDVoucher();
  }

  getFormPaymentAvailable(formOfPayments) {
    return this.cart.getFormPaymentAvailable(formOfPayments);
  }

  getCostOnlyFlight() {
    return this.cart.getCostByLegForOnHold(null);
  }

  getBalanceEMDVoucher() {
    return this.cart.getBalanceEMDVoucher();
  }

  getTotalExtras() {
    return this.cart.getTotalExtras();
  }

  hasBenefitPreferedCP() {
    return this.cart.hasBenefitPreferedCP();
  }

  getLevelsTierBenefit() {
    return this.cart.getLevelsTierBenefit();
  }

  getFFProgram() {
    return this.cart.getFFProgram();
  }

  setToken(token) {
    this.cart.setToken(token);
  }

  getLegsCancelled() {
    const legsCancelled = [];
    this.legCollection.forEach((leg, key) => {
      if (leg?.segmentsCollection?.some(segment => (segment.isCancelOag || segment.isCancelOds) || segment.segmentStatus === 'WK')) {
        leg.idx = key;
        legsCancelled.push(leg);
      }
    });

    return legsCancelled;
  }

  getItineraryCancelled() {
    let itineraryCancelled = '';
    const legsCancelled = this.getLegsCancelled();
    itineraryCancelled = legsCancelled.length && legsCancelled.map(leg => {
      return leg.segmentsCollection.length && leg.segmentsCollection.map(element => {
        if ((element.isCancelOag || element.isCancelOds) || element.segmentStatus === 'WK') {
          return `${element.departureAirport} - ${element.arrivalAirport}`;
        }
      });
    }).join(', ');

    return itineraryCancelled;
  }

  getLegsNotCancelled() {
    const legsNotCancelled = [];
    this.legCollection.forEach((leg, key) => {
      if (leg?.segmentsCollection?.some(segment => !segment.isCancelOag && !segment.isCancelOds && segment.segmentStatus !== 'WK')) {
        leg.idx = key;
        legsNotCancelled.push(leg);
      }
    });

    return legsNotCancelled;
  }

  _getIsFareLock() {
    let hasFareLock = false;

    if (this?.cart?.travelerCollection) {
      const {
        travelerCollection,
      } = this.cart;

      // eslint-disable-next-line no-unused-expressions
      travelerCollection?.forEach(({ ancillaries }) => {
        const {
          ancillaries: travelerAncillaries,
        } = ancillaries;
        const hasFareLockPassenger = travelerAncillaries.some((ac) => ac.type === 'KYF' && ac.isPaid);

        if (hasFareLockPassenger) {
          hasFareLock = hasFareLockPassenger;
        }
      });
    }
    return hasFareLock;
  }

  checkLegsDomestic() {
    return this.legCollection?.some(leg => leg.marketFlight === 'DOMESTIC' && leg.manageStatus === 'OPEN');
  }

  isFareBasic() {
    return this.legCollection?.some(leg => {
      return leg?.segmentsCollection?.some(segment => segment?.bookingClass === 'V');
    });
  }

  isUnaccompaniedMinor() {
    return this.remarks?.some(remark => remark.includes('UMNR'));
  }

  isGroupReservation() {
    const cart = this.cart;
    const travelers = cart.getAllPassengers();
    const totInfant = travelers.filter(traveler => traveler?.infant).length;
    const totPasajeros = travelers.length + totInfant;

    return totPasajeros > 7;
  }

  isReservationEmployee() {
    const bookingClassEmployee = ['G', 'P', 'A', 'Z'];
    return this.legCollection?.some(leg => {
      return leg?.segmentsCollection?.some(segment => bookingClassEmployee?.includes(segment?.bookingClass));
    });
  }

  isReservationCodeShare() {
    return this.legCollection?.some(leg => {
      return leg?.segmentsCollection?.some(segment => segment?.codeShare);
    });
  }

  /**
   * Check if all travelers have already purchased specific ancillary types
   * @param {string|string[]} ancillaryTypes - The ancillary type(s) to check (e.g., '0CZ' or ['0CZ', '0MJ'])
   * @returns {boolean} - true if all travelers have purchased any of the ancillaries
   * */
  hasAlreadyPurchasedAncillary(ancillaryTypes) {
    const types = Array.isArray(ancillaryTypes) ? ancillaryTypes : [ancillaryTypes];
    const travelers = this.cart.getAllPassengers();
    return travelers.every(traveler => {
      const ancillaries = traveler?.ancillaries?.ancillaries || [];
      if (ancillaries?.length === 0) {
        return false;
      }
      return ancillaries.some(ancillary => {
        return types.includes(ancillary?.type) && ancillary?.isPaid;
      });
    });
  }
}

export default PNRUIModel;
